import React from 'react';
import { AssetMetadata, StorageTier } from '@linein/shared';
import { Image, Video, Youtube, Instagram, Check, HardDrive, AlertTriangle, Star } from 'lucide-react';

interface AssetCardProps {
  asset: AssetMetadata;
  selected: boolean;
  onSelect: (e: React.MouseEvent) => void;
  onClick: (asset: AssetMetadata) => void;
  onToggleStar: (asset: AssetMetadata) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({ asset, selected, onSelect, onClick, onToggleStar }) => {
  const getIcon = () => {
    switch (asset.sourceType) {
      case 'IMAGE':
        return <Image size={14} className="text-blue-500" />;
      case 'VIDEO':
        return <Video size={14} className="text-purple-500" />;
      case 'YOUTUBE':
        return <Youtube size={14} className="text-red-500" />;
      case 'INSTAGRAM':
        return <Instagram size={14} className="text-pink-500" />;
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('assetId', asset.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className={`group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer border-2 ${selected ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-slate-100 hover:border-slate-200'} active:scale-95`}
      style={{ width: '100%' }}
      onClick={() => onClick(asset)}
    >
      {/* Selection Overlay */}
      <div
        className={`absolute top-3 left-3 z-20 w-6 h-6 rounded-lg flex items-center justify-center transition-all ${selected ? 'bg-blue-600 scale-110 shadow-lg' : 'bg-black/20 opacity-0 group-hover:opacity-100 backdrop-blur-sm'}`}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(e);
        }}
      >
        <Check
          size={16}
          className={`text-white transition-transform ${selected ? 'scale-100' : 'scale-0'}`}
        />
      </div>

      <button
        className="absolute top-3 right-3 z-20 w-7 h-7 rounded-lg flex items-center justify-center bg-black/25 hover:bg-black/45 backdrop-blur-sm transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          onToggleStar(asset);
        }}
        title={asset.isStarred ? '移除星號' : '加入星號'}
      >
        <Star size={15} className={asset.isStarred ? 'text-amber-300 fill-amber-300' : 'text-white'} />
      </button>

      {/* Media Content */}
      <div className="aspect-square w-full overflow-hidden bg-slate-100 relative">
        {asset.thumbnailUrl ? (
          <img
            src={asset.thumbnailUrl}
            alt={asset.originalFilename}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 pointer-events-none"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-slate-200">
            <HardDrive size={32} className="text-slate-400" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors"></div>

        {/* Tags badge (single-line ellipsis) */}
        {asset.tags && asset.tags.length > 0 && (
          <div
            className="absolute bottom-2 right-2 max-w-[78%] bg-black/55 backdrop-blur-sm text-white text-[9px] font-bold px-2 py-0.5 rounded-md truncate"
            title={asset.tags.join(', ')}
          >
            {asset.tags.map((tag) => `#${tag}`).join(' ')}
          </div>
        )}

        {/* Unavailable overlay */}
        {asset.originalAvailable === false && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="flex flex-col items-center gap-1">
              <AlertTriangle size={20} className="text-amber-400" />
              <span className="text-white text-[10px] font-black">原始檔不可用</span>
            </div>
          </div>
        )}
      </div>

      {/* Compact Info Section */}
      <div className="p-2 flex items-center gap-2 bg-white/95 backdrop-blur-sm">
        <div className="shrink-0 opacity-70">{getIcon()}</div>
        <h3
          className="flex-1 font-bold text-slate-800 truncate text-[11px] antialiased"
          title={asset.originalFilename}
        >
          {asset.originalFilename}
        </h3>
        {asset.storageTier === StorageTier.NAS_INDEXED && (
          <div className="shrink-0 flex items-center gap-1 bg-violet-50 text-violet-700 text-[9px] font-black px-1.5 py-0.5 rounded-md border border-violet-200">
            <HardDrive size={9} />
            NAS
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetCard;
